import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Link2, Link2Off, FileSpreadsheet, RefreshCw, Settings, Check,
  AlertTriangle, ChevronRight, Eye, Columns, Table2, ShieldCheck, Loader2,
  ExternalLink, X, Info, Database, Cloud, Zap, Clock, User, Building2,
  CheckCircle2, Circle, AlertCircle, Play, Pause, History, RotateCcw,
  FileText, Shield, Lock, Sparkles, ArrowRight, Plus, Search, Filter,
  ChevronDown, ChevronLeft, Rows, Hash, Calendar, DollarSign, Tag
} from 'lucide-react';

// ============ INTEGRATIONS CONFIG ============
const INTEGRATIONS = {
  google_sheets: {
    id: 'google_sheets',
    name: 'Google Drive',
    description: 'Conecta tu cuenta de Google Drive para usar tus archivos en FinScope',
    icon: ({ className }) => (
      <svg viewBox="0 0 24 24" className={className}>
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
    category: 'spreadsheet',
    available: true,
    popular: true,
  },
  excel: {
    id: 'excel',
    name: 'Microsoft Excel',
    description: 'Importa archivos Excel desde OneDrive o local',
    icon: ({ className }) => (
      <svg viewBox="0 0 24 24" className={className}>
        <path fill="#217346" d="M23 1.5q.41 0 .7.3.3.29.3.7v19q0 .41-.3.7-.29.3-.7.3H7q-.41 0-.7-.3-.3-.29-.3-.7V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h5V2.5q0-.41.3-.7.29-.3.7-.3zM6 13.28l1.42 2.66h2.14l-2.38-3.87 2.34-3.8H7.46l-1.3 2.4-.05.08-.04.09-.64-1.28-.66-1.29H2.59l2.27 3.82-2.48 3.85h2.16zM14.25 21v-3H7.5v3zm0-4.5v-3.75H12v3.75zm0-5.25V7.5H12v3.75zm0-5.25V3H7.5v3zm8.25 15v-3h-6.75v3zm0-4.5v-3.75h-6.75v3.75zm0-5.25V7.5h-6.75v3.75zm0-5.25V3h-6.75v3z"/>
      </svg>
    ),
    category: 'spreadsheet',
    available: false,
    comingSoon: true,
  },
  quickbooks: {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Sincroniza con tu contabilidad de QuickBooks',
    icon: ({ className }) => (
      <svg viewBox="0 0 24 24" className={className}>
        <path fill="#2CA01C" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm2.625 17.625h-1.5v-1.5h-1.5v1.5h-1.5v-1.5h-.75c-1.657 0-3-1.343-3-3s1.343-3 3-3h.75v-1.5h1.5v1.5h1.5v-1.5h1.5v1.5h.75c1.657 0 3 1.343 3 3s-1.343 3-3 3h-.75v1.5zm-3-4.5h-.75c-.827 0-1.5.673-1.5 1.5s.673 1.5 1.5 1.5h.75v-3zm4.5 0h-.75v3h.75c.827 0 1.5-.673 1.5-1.5s-.673-1.5-1.5-1.5z"/>
      </svg>
    ),
    category: 'accounting',
    available: false,
    comingSoon: true,
  },
  contpaqi: {
    id: 'contpaqi',
    name: 'CONTPAQi',
    description: 'Conecta con tu sistema CONTPAQi Contabilidad',
    icon: ({ className }) => (
      <div className={`${className} bg-blue-600 rounded flex items-center justify-center text-white font-bold text-[10px]`}>
        CQ
      </div>
    ),
    category: 'accounting',
    available: false,
    comingSoon: true,
  },
  sap: {
    id: 'sap',
    name: 'SAP',
    description: 'Integración empresarial con SAP ERP',
    icon: ({ className }) => (
      <div className={`${className} bg-[#0070F2] rounded flex items-center justify-center text-white font-bold text-[8px]`}>
        SAP
      </div>
    ),
    category: 'erp',
    available: false,
    comingSoon: true,
  },
  api_rest: {
    id: 'api_rest',
    name: 'API REST',
    description: 'Conecta cualquier sistema via API personalizada',
    icon: ({ className }) => <Zap className={className} />,
    category: 'api',
    available: false,
    comingSoon: true,
  },
  sql_server: {
    id: 'sql_server',
    name: 'SQL Server',
    description: 'Conexión directa a Microsoft SQL Server',
    icon: ({ className }) => <Database className={className} />,
    category: 'database',
    available: false,
    comingSoon: true,
  },
};

const CATEGORIES = {
  spreadsheet: { label: 'Hojas de cálculo', icon: FileSpreadsheet },
  accounting: { label: 'Contabilidad', icon: Building2 },
  erp: { label: 'ERP', icon: Database },
  database: { label: 'Bases de datos', icon: Database },
  api: { label: 'APIs', icon: Zap },
};

const INVOICE_COLUMNS = [
  { key: 'invoice_number', label: 'No. Factura', required: true, icon: Hash },
  { key: 'invoice_date', label: 'Fecha', required: true, icon: Calendar },
  { key: 'client_name', label: 'Cliente', required: true, icon: Building2 },
  { key: 'company_name', label: 'Empresa', required: false, icon: Building2 },
  { key: 'seller_name', label: 'Vendedor', required: false, icon: User },
  { key: 'responsible_name', label: 'Encargado', required: false, icon: User },
  { key: 'concept', label: 'Concepto', required: false, icon: Tag },
  { key: 'concept_key', label: 'Clave', required: false, icon: Hash },
  { key: 'description', label: 'Descripción', required: false, icon: FileText },
  { key: 'currency', label: 'Moneda', required: false, icon: DollarSign },
  { key: 'subtotal', label: 'Monto sin IVA', required: false, icon: DollarSign },
  { key: 'total_amount', label: 'Monto con IVA', required: true, icon: DollarSign },
  { key: 'collected_amount', label: 'Cobrado', required: false, icon: DollarSign },
  { key: 'invoice_status', label: 'Status', required: false, icon: Tag },
  { key: 'invoice_type', label: 'Tipo (iguala/one shot)', required: false, icon: Tag },
];

const SCHEMA_DATA_TYPES = ['facturas', 'pendiente_facturar'];

// ============ HELPER FUNCTIONS ============
function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('es-MX', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
}

function formatRelativeTime(isoString) {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Hace un momento';
    if (mins < 60) return `Hace ${mins} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days < 7) return `Hace ${days} días`;
    return formatDateTime(isoString);
  } catch { return '—'; }
}

// ============ TENANT IDENTIFIER ============
function TenantBadge({ user }) {
  const tenantName = user?.cliente_nombre || user?.empresa_nombre || user?.marca_nombre || 'Sin empresa';
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded-lg" data-testid="tenant-badge">
      <Building2 className="w-4 h-4 text-primary" />
      <div className="text-xs">
        <span className="text-muted-foreground">Empresa actual: </span>
        <span className="font-semibold text-primary">{tenantName}</span>
      </div>
    </div>
  );
}

// ============ COMING SOON CARD ============
function ComingSoonCard({ integration }) {
  const Icon = integration.icon;
  
  return (
    <Card className="relative overflow-hidden opacity-60 hover:opacity-80 transition-opacity">
      <div className="absolute top-2 right-2">
        <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500 border-gray-200">
          Próximamente
        </Badge>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-600">{integration.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{integration.description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ GOOGLE SHEETS CONNECTED CARD ============
function GoogleSheetsConnectedCard({ status, onDisconnect, onManage, onSync, syncLoading }) {
  const integration = INTEGRATIONS.google_sheets;
  const Icon = integration.icon;
  
  return (
    <Card className="relative overflow-hidden border-2 border-emerald-300 bg-emerald-50/30" data-testid="google-sheets-connected-card">
      <div className="absolute top-0 right-0">
        <div className="bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-bl-lg flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Conectado
        </div>
      </div>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-white border-2 border-emerald-200 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Icon className="w-9 h-9" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{integration.name}</h3>
            </div>
            
            {/* Connection Info */}
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Cuenta:</span>
                <span className="font-medium">{status.google_email}</span>
              </div>
              {status.google_name && (
                <div className="flex items-center gap-2 text-xs">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Nombre:</span>
                  <span className="font-medium">{status.google_name}</span>
                </div>
              )}
              {status.selected_file_name && (
                <div className="flex items-center gap-2 text-xs">
                  <FileSpreadsheet className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Archivo:</span>
                  <span className="font-medium text-emerald-700">{status.selected_file_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Conectado:</span>
                <span className="font-medium">{formatRelativeTime(status.connected_at)}</span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" onClick={onManage} className="gap-1.5 text-xs" data-testid="manage-connection-btn">
                <Settings className="w-3.5 h-3.5" />
                Configurar
              </Button>
              {status.selected_file_name && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={onSync} 
                  disabled={syncLoading}
                  className="gap-1.5 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="sync-now-btn"
                >
                  {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Sincronizar ahora
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onDisconnect} className="gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="disconnect-btn">
                <Link2Off className="w-3.5 h-3.5" />
                Desconectar
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ GOOGLE SHEETS DISCONNECTED CARD ============
function GoogleSheetsDisconnectedCard({ status, onConnect, connecting }) {
  const integration = INTEGRATIONS.google_sheets;
  const Icon = integration.icon;
  
  const isConfigured = status?.configured;
  
  return (
    <Card className="relative overflow-hidden border-2 hover:border-primary/30 transition-colors" data-testid="google-sheets-card">
      <div className="absolute top-0 right-0">
        <div className="bg-primary text-white text-[10px] font-medium px-2 py-0.5 rounded-bl-lg">
          Disponible
        </div>
      </div>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-white border-2 border-gray-100 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Icon className="w-9 h-9" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{integration.name}</h3>
              <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600 border-gray-200">
                <Circle className="w-2 h-2 mr-1" />
                Google Drive no conectado
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{integration.description}</p>
            
            {!isConfigured ? (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    OAuth no configurado en el servidor. Contacte al administrador para agregar las credenciales de Google.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">
                    Conecta tu cuenta de Google Drive. Tú eliges la cuenta y autorizas los permisos directamente en Google.
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4">
              <Button 
                size="sm" 
                onClick={onConnect} 
                disabled={!isConfigured || connecting}
                className="gap-1.5 text-xs" 
                data-testid="connect-google-btn"
              >
                {connecting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Link2 className="w-3.5 h-3.5" />
                )}
                Conectar Google Drive
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ STEP INDICATOR ============
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = idx < currentStep;
        return (
          <React.Fragment key={step.id}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              isActive ? 'bg-primary text-white' : isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
            }`}>
              {isCompleted ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <span className="w-4 h-4 rounded-full bg-current/20 flex items-center justify-center text-[10px]">
                  {idx + 1}
                </span>
              )}
              {step.label}
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============ CONFIGURATION DIALOG ============
function ConfigurationDialog({ open, onClose, status, onRefreshStatus }) {
  const { user } = useAuth();
  const isAdminUser = user?.role && ['super_admin', 'admin', 'admin_cliente'].includes(user.role);
  const [currentStep, setCurrentStep] = useState(0);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [sheetConfigs, setSheetConfigs] = useState({});
  const [columnMappings, setColumnMappings] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [verificationResults, setVerificationResults] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [smartAnalysis, setSmartAnalysis] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [importing, setImporting] = useState(false);
  
  const steps = [
    { id: 'select_file', label: 'Seleccionar archivo' },
    { id: 'select_sheets', label: 'Seleccionar pestañas' },
    { id: 'smart_mapping', label: 'Smart Mapping' },
    { id: 'ready', label: 'Listo' },
  ];
  
  // Load files on open
  useEffect(() => {
    if (open && status?.status === 'connected') {
      loadFiles();
      // If file already selected, start from sheets
      if (status.selected_file_id) {
        setSelectedFile({ id: status.selected_file_id, name: status.selected_file_name });
        setCurrentStep(1);
        loadSheets(status.selected_file_id);
      }
    }
  }, [open, status]);
  
  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/google/files');
      setFiles(res.data.files || []);
    } catch (err) {
      toast.error('Error cargando archivos de Google Drive');
    } finally {
      setLoading(false);
    }
  };
  
  const loadSheets = async (fileId) => {
    setLoading(true);
    try {
      const res = await api.get(`/google/files/${fileId}/sheets`);
      setSheets(res.data || []);
      
      // Load existing configs
      const configRes = await api.get('/google/sheet-configs');
      const configs = configRes.data || [];
      const fileConfigs = configs.filter(c => c.file_id === fileId);
      
      // Pre-select configured sheets
      const activeSheets = fileConfigs.filter(c => c.active).map(c => c.sheet_name);
      setSelectedSheets(activeSheets);
      
      // Load configs and mappings
      const configMap = {};
      const mappingMap = {};
      fileConfigs.forEach(c => {
        configMap[c.sheet_name] = { headerRow: c.header_row, dataStartRow: c.data_start_row, dataType: c.data_type };
        mappingMap[c.sheet_name] = c.column_mapping || {};
      });
      setSheetConfigs(configMap);
      setColumnMappings(mappingMap);
      
    } catch (err) {
      toast.error('Error cargando pestañas del archivo');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectFile = async (file) => {
    setLoading(true);
    try {
      await api.post(`/google/files/${file.id}/select`);
      setSelectedFile(file);
      await loadSheets(file.id);
      setCurrentStep(1);
      onRefreshStatus();
    } catch (err) {
      toast.error('Error seleccionando archivo');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleSheet = (sheetName) => {
    setSelectedSheets(prev => {
      if (prev.includes(sheetName)) {
        return prev.filter(s => s !== sheetName);
      }
      return [...prev, sheetName];
    });
  };
  
  const loadPreview = async (sheetName) => {
    const config = sheetConfigs[sheetName] || { headerRow: 1, dataStartRow: 2 };
    try {
      const res = await api.get(`/google/files/${selectedFile.id}/sheets/${encodeURIComponent(sheetName)}/preview`, {
        params: { header_row: config.headerRow, data_start_row: config.dataStartRow, max_rows: 5 }
      });
      setPreviewData({ sheetName, ...res.data });
    } catch (err) {
      toast.error('Error cargando vista previa');
    }
  };
  
  const saveSheetConfig = async (sheetName) => {
    const config = sheetConfigs[sheetName] || { headerRow: 1, dataStartRow: 2, dataType: 'facturas' };
    const mapping = columnMappings[sheetName] || {};
    
    try {
      await api.post('/google/sheet-configs', {
        file_id: selectedFile.id,
        sheet_name: sheetName,
        active: selectedSheets.includes(sheetName),
        data_type: config.dataType || 'facturas',
        header_row: config.headerRow || 1,
        data_start_row: config.dataStartRow || 2,
        column_mapping: mapping,
      });
      toast.success(`Configuración de "${sheetName}" guardada`);
    } catch (err) {
      toast.error('Error guardando configuración');
    }
  };
  
  const runSmartAnalysis = async (sheetNames, dataTypeOverride = null) => {
    setAnalyzing(true);
    setCurrentStep(2);
    const results = {};
    try {
      for (const sheetName of sheetNames) {
        const res = await api.get(`/google/files/${selectedFile.id}/sheets/${encodeURIComponent(sheetName)}/smart-mapping`,
          dataTypeOverride ? { params: { data_type: dataTypeOverride } } : undefined);
        results[sheetName] = res.data;
        const autoMapping = {};
        (res.data.mapping || []).forEach(m => { if (m.column) autoMapping[m.field] = m.column; });
        setColumnMappings(prev => ({
          ...prev,
          [sheetName]: dataTypeOverride ? autoMapping : { ...autoMapping, ...(prev[sheetName] || {}) },
        }));
        setSheetConfigs(prev => ({
          ...prev,
          [sheetName]: {
            headerRow: prev[sheetName]?.headerRow || res.data.header_row,
            dataStartRow: prev[sheetName]?.dataStartRow || res.data.data_start_row,
            dataType: res.data.data_type || prev[sheetName]?.dataType || 'facturas',
          }
        }));
      }
      setSmartAnalysis(results);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error analizando el archivo');
    } finally {
      setAnalyzing(false);
    }
  };

  const runDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const results = {};
      for (const sheetName of selectedSheets) {
        const res = await api.get(`/google/files/${selectedFile.id}/sheets/${encodeURIComponent(sheetName)}/mapping-diagnostic`);
        results[sheetName] = res.data;
      }
      setDiagData(results);
      setShowDiag(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando el diagnóstico');
    } finally {
      setDiagLoading(false);
    }
  };

  const copyDiagnostic = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagData, null, 2));
      toast.success('Diagnóstico copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar. Selecciona el texto manualmente.');
    }
  };

  const confirmAndImport = async () => {
    setImporting(true);
    setSyncResult(null);
    setVerificationResults(null);
    try {
      for (const sheetName of selectedSheets) {
        await saveSheetConfig(sheetName);
      }
      const sheetName = selectedSheets[0];
      const config = sheetConfigs[sheetName] || { headerRow: 1, dataStartRow: 2 };
      const res = await api.post('/facturacion/verify-structure', null, {
        params: {
          file_id: selectedFile.id,
          sheet_name: sheetName,
          header_row: config.headerRow || 1,
          data_start_row: config.dataStartRow || 2,
        }
      });
      setVerificationResults(res.data);
      const blockers = res.data?.checklist?.some(c => c.status === 'error' && c.severity === 'blocker');
      if (!blockers) {
        try {
          const syncRes = await api.post('/facturacion/sync/manual');
          setSyncResult(syncRes.data);
          toast.success('Importación completada');
        } catch (syncErr) {
          toast.error(syncErr.response?.data?.detail || 'Error al importar');
        }
      } else {
        toast.error('Hay errores bloqueantes en la estructura');
      }
      setCurrentStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error confirmando la configuración');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFiles([]);
    setSelectedFile(null);
    setSheets([]);
    setSelectedSheets([]);
    setSheetConfigs({});
    setColumnMappings({});
    setPreviewData(null);
    setVerificationResults(null);
    setSmartAnalysis({});
    setSyncResult(null);
    setAdvancedOpen(false);
    onClose();
  };
  
  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'select_file':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Archivos de Google Sheets</h3>
                <p className="text-xs text-muted-foreground">Selecciona el archivo que contiene tus datos de facturación</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadFiles} disabled={loading} className="gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
            
            {loading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No se encontraron archivos Google Sheets</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {files.map(file => (
                  <div
                    key={file.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
                      selectedFile?.id === file.id ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                    onClick={() => handleSelectFile(file)}
                    data-testid={`file-${file.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {file.owner} · Modificado: {formatRelativeTime(file.modified_at)}
                        </p>
                      </div>
                    </div>
                    {selectedFile?.id === file.id && <Check className="w-5 h-5 text-primary" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        
      case 'select_sheets':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Seleccionar pestañas</h3>
              <p className="text-xs text-muted-foreground">Activa las pestañas que deseas importar de &quot;{selectedFile?.name}&quot;</p>
            </div>
            
            {loading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : (
              <div className="space-y-2">
                {sheets.map(sheet => (
                  <div
                    key={sheet.sheet_id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      selectedSheets.includes(sheet.title) ? 'border-primary/50 bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Table2 className={`w-5 h-5 ${selectedSheets.includes(sheet.title) ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="text-sm font-medium">{sheet.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{sheet.row_count?.toLocaleString()} filas</span>
                          <span>•</span>
                          <span>{sheet.column_count} columnas</span>
                          {sheet.has_merges && (
                            <>
                              <span>•</span>
                              <span className="text-amber-600 flex items-center gap-0.5">
                                <AlertTriangle className="w-3 h-3" /> {sheet.merge_count} celdas combinadas
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={selectedSheets.includes(sheet.title)}
                      onCheckedChange={() => handleToggleSheet(sheet.title)}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {selectedSheets.length > 0 && (
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                {selectedSheets.length} pestaña(s) seleccionada(s): {selectedSheets.join(', ')}
              </div>
            )}
            
            <Button onClick={() => runSmartAnalysis(selectedSheets)} disabled={selectedSheets.length === 0} className="w-full gap-2" data-testid="analyze-sheets-btn">
              <Sparkles className="w-4 h-4" />
              Analizar con Smart Mapping
            </Button>
          </div>
        );
        
      case 'smart_mapping': {
        const requiredMissing = selectedSheets.some(s => {
          const analysis = smartAnalysis[s];
          if (!analysis) return true;
          return (analysis.mapping || []).some(m => m.required && !(columnMappings[s]?.[m.field] ?? m.column));
        });
        const confBadge = (m, current) => {
          if (!current) return <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Selecciona</Badge>;
          if (current !== m.column) return <Badge variant="outline" className="text-[10px]">Manual</Badge>;
          if (m.action === 'auto') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">✓ {Math.round(m.confidence * 100)}%</Badge>;
          if (m.action === 'review') return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Revisar · {Math.round(m.confidence * 100)}%</Badge>;
          return <Badge variant="outline" className="text-[10px]">Manual</Badge>;
        };
        return (
          <div className="space-y-4" data-testid="smart-mapping-step">
            {analyzing ? (
              <div className="text-center py-10">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-primary animate-pulse" />
                <p className="text-sm font-medium">Analizando tu archivo...</p>
                <p className="text-xs text-muted-foreground mt-1">Detectando encabezados, tipos de datos y columnas</p>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold" data-testid="smart-mapping-title">Hemos analizado tu archivo</h3>
                    <p className="text-xs text-muted-foreground">Smart Mapping detectó las columnas automáticamente. Revisa las marcadas en ámbar/rojo y confirma.</p>
                  </div>
                  {isAdminUser && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 shrink-0"
                      onClick={runDiagnostic}
                      disabled={diagLoading || selectedSheets.length === 0}
                      data-testid="mapping-diagnostic-btn"
                    >
                      {diagLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                      Diagnóstico
                    </Button>
                  )}
                </div>

                {selectedSheets.map(sheetName => {
                  const analysis = smartAnalysis[sheetName];
                  if (!analysis) return null;
                  const headers = (analysis.columns || []).map(c => c.column);
                  return (
                    <Card key={sheetName}>
                      <CardContent className="p-4">
                        <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                          <Table2 className="w-4 h-4" /> {sheetName}
                        </h4>
                        <div className="grid grid-cols-[minmax(90px,1fr)_1.5fr_auto] gap-x-3 gap-y-2 items-center text-xs">
                          <span className="font-medium text-muted-foreground">Campo FinScope</span>
                          <span className="font-medium text-muted-foreground">Columna detectada</span>
                          <span className="font-medium text-muted-foreground">Confianza</span>
                          {(analysis.mapping || []).map(m => {
                            const current = columnMappings[sheetName]?.[m.field] !== undefined
                              ? columnMappings[sheetName][m.field]
                              : m.column;
                            return (
                              <React.Fragment key={m.field}>
                                <span className="flex items-center gap-1 truncate">
                                  {m.label}{m.required && <span className="text-red-500">*</span>}
                                </span>
                                <Select
                                  value={current || '_none'}
                                  onValueChange={(v) => setColumnMappings(prev => ({
                                    ...prev,
                                    [sheetName]: { ...prev[sheetName], [m.field]: v === '_none' ? null : v }
                                  }))}
                                >
                                  <SelectTrigger className={`h-7 text-xs ${m.required && !current ? 'border-red-300' : ''}`} data-testid={`smart-select-${m.field}`}>
                                    <SelectValue placeholder="— Selecciona columna —" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_none">— Sin mapear —</SelectItem>
                                    {headers.map((h, i) => <SelectItem key={i} value={h}>{h}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <div>{confBadge(m, current)}</div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                <div className="border rounded-lg">
                  <button
                    type="button"
                    onClick={() => setAdvancedOpen(!advancedOpen)}
                    className="w-full flex items-center justify-between p-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="advanced-config-toggle"
                  >
                    Configuración avanzada
                    <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {advancedOpen && (
                    <div className="p-3 pt-0 space-y-3" data-testid="advanced-config-panel">
                      {selectedSheets.map(sheetName => (
                        <div key={sheetName} className="grid grid-cols-3 gap-3 items-end">
                          <div>
                            <Label className="text-[10px]">{sheetName} · Fila encabezados</Label>
                            <Input
                              type="number" min={1}
                              value={sheetConfigs[sheetName]?.headerRow || 1}
                              className="h-7 text-xs mt-1"
                              onChange={(e) => setSheetConfigs(prev => ({
                                ...prev, [sheetName]: { ...prev[sheetName], headerRow: parseInt(e.target.value) || 1 }
                              }))}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Primera fila de datos</Label>
                            <Input
                              type="number" min={2}
                              value={sheetConfigs[sheetName]?.dataStartRow || 2}
                              className="h-7 text-xs mt-1"
                              onChange={(e) => setSheetConfigs(prev => ({
                                ...prev, [sheetName]: { ...prev[sheetName], dataStartRow: parseInt(e.target.value) || 2 }
                              }))}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Tipo de datos</Label>
                            <Select
                              value={sheetConfigs[sheetName]?.dataType || 'facturas'}
                              onValueChange={(v) => {
                                setSheetConfigs(prev => ({
                                  ...prev, [sheetName]: { ...prev[sheetName], dataType: v }
                                }));
                                if (SCHEMA_DATA_TYPES.includes(v)) runSmartAnalysis([sheetName], v);
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="facturas">Facturas</SelectItem>
                                <SelectItem value="pendiente_facturar">Pendiente de facturar</SelectItem>
                                <SelectItem value="cobros">Cobros</SelectItem>
                                <SelectItem value="clientes">Clientes</SelectItem>
                                <SelectItem value="catalogos">Catálogos</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                      <p className="text-[10px] text-muted-foreground">
                        El mapeo manual campo por campo sigue disponible: cambia cualquier columna en los selectores de arriba.
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={confirmAndImport}
                  disabled={importing || requiredMissing}
                  className="w-full gap-2"
                  data-testid="confirm-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {importing ? 'Importando...' : 'Confirmar configuración e importar'}
                </Button>
                {requiredMissing && !analyzing && (
                  <p className="text-[11px] text-red-600 text-center">Selecciona una columna para los campos obligatorios (*)</p>
                )}
              </>
            )}
          </div>
        );
      }

      case 'ready':
        const hasBlockers = verificationResults?.checklist?.some(c => c.status === 'error' && c.severity === 'blocker');
        
        return (
          <div className="space-y-4">
            {verificationResults?.checklist && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="text-sm font-semibold mb-3">Resultados de verificación</h4>
                  <div className="space-y-2">
                    {verificationResults.checklist.map((check) => (
                      <div
                        key={check.id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          check.status === 'passed' ? 'bg-emerald-50' :
                          check.status === 'error' ? 'bg-red-50' :
                          'bg-amber-50'
                        }`}
                      >
                        {check.status === 'passed' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        ) : check.status === 'error' ? (
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        )}
                        <span className={`text-xs flex-1 ${
                          check.status === 'passed' ? 'text-emerald-700' :
                          check.status === 'error' ? 'text-red-700' :
                          'text-amber-700'
                        }`}>
                          {check.label}
                        </span>
                        <Badge variant="outline" className={`text-[9px] ${
                          check.severity === 'blocker' ? 'border-red-300 text-red-600' :
                          check.severity === 'warning' ? 'border-amber-300 text-amber-600' :
                          'border-gray-300 text-gray-500'
                        }`}>
                          {check.severity === 'blocker' ? 'Bloqueante' : check.severity === 'warning' ? 'Advertencia' : 'Info'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className={hasBlockers ? 'border-red-200 bg-red-50/50' : 'border-emerald-200 bg-emerald-50/50'}>
              <CardContent className="p-6 text-center">
                {hasBlockers ? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Configuración incompleta</h3>
                    <p className="text-sm text-red-700 mb-4">
                      Se detectaron errores bloqueantes que deben corregirse antes de sincronizar.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-emerald-800 mb-2">
                      {syncResult ? 'Importación completada' : 'Listo para sincronizar'}
                    </h3>
                    <p className="text-sm text-emerald-700 mb-4">
                      {syncResult
                        ? `${syncResult.rows_read ?? 0} filas leídas · ${syncResult.invoices_created ?? 0} facturas creadas · ${syncResult.invoices_updated ?? 0} actualizadas`
                        : 'La estructura del archivo ha sido validada. Puedes ejecutar la sincronización manual.'}
                    </p>
                  </>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-xs text-left mb-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <span className="text-muted-foreground">Archivo</span>
                    <p className="font-medium truncate">{selectedFile?.name}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <span className="text-muted-foreground">Pestañas</span>
                    <p className="font-medium">{selectedSheets.length} configuradas</p>
                  </div>
                </div>
                
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setCurrentStep(0)} className="gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Cambiar archivo
                  </Button>
                  <Button onClick={handleClose} className="gap-2" data-testid="finish-config-btn">
                    <Check className="w-4 h-4" />
                    Finalizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Configurar Google Sheets
          </DialogTitle>
          <DialogDescription>
            Archivo: {selectedFile?.name || 'Sin seleccionar'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-1">
          <StepIndicator currentStep={currentStep} steps={steps} />
          {renderStepContent()}
        </div>
        
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} className="gap-2">
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      <Dialog open={showDiag} onOpenChange={setShowDiag}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="mapping-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-primary" />
              Diagnóstico Smart Mapping (solo lectura)
            </DialogTitle>
            <DialogDescription>
              Contenido sanitizado que recibe el pipeline: extracción → encabezados → profiler → motor de mapping.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto rounded-lg border bg-slate-950 p-3">
            <pre className="text-[10px] leading-relaxed text-emerald-300 whitespace-pre-wrap break-all" data-testid="mapping-diagnostic-json">
              {diagData ? JSON.stringify(diagData, null, 2) : ''}
            </pre>
          </div>
          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setShowDiag(false)}>Cerrar</Button>
            <Button onClick={copyDiagnostic} className="gap-2" data-testid="copy-diagnostic-btn">
              <Check className="w-4 h-4" /> Copiar JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// ============ SYNC HISTORY DIALOG ============
function SyncHistoryDialog({ open, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (open) {
      loadHistory();
    }
  }, [open]);
  
  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/facturacion/sync/history');
      setHistory(res.data || []);
    } catch (err) {
      toast.error('Error cargando historial');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de sincronizaciones
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay sincronizaciones registradas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={h.status === 'success' ? 'default' : h.status === 'failed' ? 'destructive' : 'secondary'}>
                      {h.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatDateTime(h.started_at)}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Creadas:</span>
                      <p className="font-medium">{h.invoices_created}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Actualizadas:</span>
                      <p className="font-medium">{h.invoices_updated}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Filas:</span>
                      <p className="font-medium">{h.rows_read}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duración:</span>
                      <p className="font-medium">{h.duration_seconds}s</p>
                    </div>
                  </div>
                  {h.errors?.length > 0 && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                      {h.errors[0]?.message || 'Error durante sincronización'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN COMPONENT ============
export default function DataConnections({ onBack }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  const isAdmin = user?.role && ['super_admin', 'admin', 'admin_cliente'].includes(user.role);
  
  // Load Google connection status
  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/google/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Error loading Google status:', err);
      if (err.response?.status === 400) {
        setStatus({ status: 'tenant_required', configured: true, detail: err.response?.data?.detail });
      } else {
        setStatus({ status: 'disconnected', configured: false });
      }
    }
  }, []);
  
  useEffect(() => {
    const init = async () => {
      await loadStatus();
      setLoading(false);
      
      // Check for OAuth callback result
      const googleConnected = searchParams.get('google_connected');
      const googleError = searchParams.get('google_error');
      const errorDetail = searchParams.get('error_detail');
      const email = searchParams.get('email');
      
      if (googleConnected === 'true') {
        toast.success(`Cuenta conectada: ${email || 'Google'}`);
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
        await loadStatus();
      } else if (googleError) {
        // Show detailed error if available
        const errorMessage = errorDetail 
          ? `Error de conexión: ${decodeURIComponent(errorDetail)}`
          : `Error de conexión: ${googleError}`;
        toast.error(errorMessage, { duration: 10000 });
        console.error('Google OAuth error:', googleError, errorDetail ? decodeURIComponent(errorDetail) : '');
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    init();
  }, [loadStatus, searchParams]);
  
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/google/connect');
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error iniciando conexión con Google');
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async () => {
    if (!confirm('¿Desconectar cuenta de Google? Esto no eliminará los datos ya importados.')) return;
    
    try {
      await api.post('/google/disconnect');
      toast.success('Cuenta de Google desconectada');
      await loadStatus();
    } catch (err) {
      toast.error('Error desconectando cuenta');
    }
  };
  
  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const res = await api.post('/facturacion/sync/manual');
      const result = res.data;
      
      if (result.status === 'success' || result.status === 'success_with_warnings') {
        toast.success(
          `Sincronización completada: ${result.invoices_created} creadas, ${result.invoices_updated} actualizadas`
        );
      } else {
        toast.error('Sincronización falló. Revisa el historial para más detalles.');
      }
      
      setShowHistoryDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error durante sincronización');
    } finally {
      setSyncLoading(false);
    }
  };
  
  // Filter integrations
  const filteredIntegrations = Object.values(INTEGRATIONS).filter(int => {
    if (!int.comingSoon) return false;
    if (categoryFilter !== 'all' && int.category !== categoryFilter) return false;
    if (searchQuery && !int.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Solo los administradores pueden configurar conexiones de datos.</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }
  
  const isConnected = status?.status === 'connected';
  
  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="data-connections-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8" data-testid="connections-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Conexión de Datos
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Hub universal de integraciones para tu módulo de facturación
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TenantBadge user={user} />
          {isConnected && (
            <Button variant="outline" size="sm" onClick={() => setShowHistoryDialog(true)} className="gap-1.5">
              <History className="w-3.5 h-3.5" />
              Historial
            </Button>
          )}
        </div>
      </div>
      
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Buscar integración..." 
            className="pl-9 h-9 text-sm"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="search-integrations"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[180px] h-9 text-sm" data-testid="filter-category">
            <Filter className="w-3.5 h-3.5 mr-2" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent position="popper">
            <SelectItem value="all">Todas las categorías</SelectItem>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <SelectItem key={key} value={key}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Main Google Sheets Card */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> {isConnected ? 'Conectado' : 'Disponible'}
        </h2>
        
        {status?.status === 'tenant_required' ? (
          <Card className="border-amber-200 bg-amber-50/50" data-testid="tenant-required-card">
            <CardContent className="p-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Selecciona una empresa para operar la conexión de Google</p>
                <p className="text-xs text-amber-700 mt-1">
                  {status.detail || 'Como Super Admin debes entrar a una marca/cliente antes de conectar Google Drive. La conexión siempre pertenece a un cliente específico.'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : isConnected ? (
          <GoogleSheetsConnectedCard 
            status={status}
            onDisconnect={handleDisconnect}
            onManage={() => setShowConfigDialog(true)}
            onSync={handleSync}
            syncLoading={syncLoading}
          />
        ) : (
          <GoogleSheetsDisconnectedCard 
            status={status}
            onConnect={handleConnect}
            connecting={connecting}
          />
        )}
      </div>
      
      {/* Coming Soon Integrations */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Próximamente
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredIntegrations.map(integration => (
            <ComingSoonCard key={integration.id} integration={integration} />
          ))}
        </div>
        
        {filteredIntegrations.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Database className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No se encontraron integraciones con ese filtro</p>
          </div>
        )}
      </div>
      
      {/* Integration Categories Legend */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Categorías de integración
          </h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(CATEGORIES).map(([key, cat]) => {
              const Icon = cat.icon;
              return (
                <div key={key} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon className="w-4 h-4" />
                  <span>{cat.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Configuration Dialog */}
      <ConfigurationDialog 
        open={showConfigDialog}
        onClose={() => setShowConfigDialog(false)}
        status={status}
        onRefreshStatus={loadStatus}
      />
      
      {/* Sync History Dialog */}
      <SyncHistoryDialog 
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
      />
    </div>
  );
}
